import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, Select, Button, Tag, Modal, Segmented, Alert, Space, Typography,
  Form, Radio, App as AntApp,
} from 'antd';
import {
  SearchOutlined, EditOutlined, StopOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Categoria {
  categoria_contaazul: string;
  linha_dre: string;
  grupo_dre: string;
  ordem: number;
  sinal: number;
  status: 'mapeada' | 'ignorada' | 'pendente';
  n_lancamentos: string;
}

const GRUPOS_OPCOES = [
  'CUSTOS_OPERACIONAIS',
  'DESPESAS_COMERCIAIS',
  'DESPESAS_ADMINISTRATIVAS',
  'OUTRAS_DESPESAS_OPERACIONAIS',
  'OUTRAS_RECEITAS_OPERACIONAIS',
  'DESPESAS_FINANCEIRAS',
  'RECEITAS_FINANCEIRAS',
  'IMPOSTOS',
  'INVESTIMENTOS',
  'COMISSOES',
  'ESTORNO',
];

type StatusFiltro = 'pendente' | 'mapeada' | 'ignorada' | 'todas';

function CategoriasInner() {
  const { message } = AntApp.useApp();
  const [cats, setCats] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<StatusFiltro>('pendente');
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<Categoria | null>(null);
  const [form, setForm] = useState<{ grupo_dre: string; linha_dre: string; status: string }>({
    grupo_dre: '', linha_dre: '', status: 'mapeada',
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/categorias`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setCats(data);
    } finally { setLoading(false); }
  }

  function abrirEdicao(c: Categoria) {
    setEditando(c);
    setForm({
      grupo_dre: c.status === 'pendente' ? '' : c.grupo_dre,
      linha_dre: c.status === 'pendente' ? c.categoria_contaazul : c.linha_dre,
      status: c.status === 'pendente' ? 'mapeada' : c.status,
    });
  }

  async function salvar() {
    if (!editando) return;
    if (form.status === 'mapeada' && (!form.grupo_dre || !form.linha_dre)) {
      message.warning('Para marcar como "Apresentar na DRE", informe grupo e linha.');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.patch(
        `${API_URL}/categorias/${encodeURIComponent(editando.categoria_contaazul)}`,
        form,
        { headers: { Authorization: token } }
      );
      message.success('Categoria atualizada.');
      setEditando(null);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function mudarStatus(cat: string, novoStatus: 'ignorada' | 'mapeada') {
    try {
      const token = await getToken();
      await axios.patch(
        `${API_URL}/categorias/${encodeURIComponent(cat)}`,
        { status: novoStatus },
        { headers: { Authorization: token } }
      );
      await carregar();
      message.success(novoStatus === 'ignorada' ? 'Categoria ignorada.' : 'Categoria reativada.');
    } catch (e: any) {
      // Mostra erro real do backend pra facilitar diagnóstico (NULL constraint
      // violation, etc.) em vez do genérico "Falha ao alterar status".
      const msg = e?.response?.data?.error || e?.response?.data?.erro || e?.message || 'erro desconhecido';
      message.error('Falha ao alterar status: ' + msg);
    }
  }

  const filtradas = cats.filter(c => {
    if (filtroStatus !== 'todas' && c.status !== filtroStatus) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return c.categoria_contaazul.toLowerCase().includes(q) ||
             c.linha_dre.toLowerCase().includes(q) ||
             c.grupo_dre.toLowerCase().includes(q);
    }
    return true;
  });

  const countByStatus = {
    pendente: cats.filter(c => c.status === 'pendente').length,
    mapeada: cats.filter(c => c.status === 'mapeada').length,
    ignorada: cats.filter(c => c.status === 'ignorada').length,
  };

  const columns: ColumnsType<Categoria> = [
    {
      title: 'Categoria (ContaAzul)',
      dataIndex: 'categoria_contaazul',
      key: 'cat',
      width: 280,
      render: (v) => <Text strong style={{ color: '#020D7F' }}>{v}</Text>,
    },
    {
      title: 'Linha DRE',
      dataIndex: 'linha_dre',
      key: 'linha',
      render: (v, r) => r.status === 'pendente'
        ? <Text type="secondary">—</Text>
        : <span>{v}</span>,
    },
    {
      title: 'Grupo DRE',
      dataIndex: 'grupo_dre',
      key: 'grupo',
      width: 240,
      render: (v, r) => r.status === 'pendente'
        ? <Text type="secondary">—</Text>
        : <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: 'Lançamentos',
      dataIndex: 'n_lancamentos',
      key: 'n',
      width: 100,
      align: 'center',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      align: 'center',
      render: (s: string) => <StatusTag status={s} />,
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 210,
      align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => abrirEdicao(r)}>
            Editar
          </Button>
          {r.status === 'pendente' && (
            <Button size="small" danger icon={<StopOutlined />}
              onClick={() => mudarStatus(r.categoria_contaazul, 'ignorada')}>
              Ignorar
            </Button>
          )}
          {r.status === 'ignorada' && (
            <Button size="small" icon={<CheckCircleOutlined />}
              onClick={() => mudarStatus(r.categoria_contaazul, 'mapeada')}>
              Reativar
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>Categorias DRE</Title>
          <Space wrap>
            <Segmented
              value={filtroStatus}
              onChange={(v) => setFiltroStatus(v as StatusFiltro)}
              options={[
                { label: `Pendentes${countByStatus.pendente > 0 ? ` (${countByStatus.pendente})` : ''}`, value: 'pendente' },
                { label: `Mapeadas (${countByStatus.mapeada})`, value: 'mapeada' },
                { label: `Ignoradas (${countByStatus.ignorada})`, value: 'ignorada' },
                { label: 'Todas', value: 'todas' },
              ]}
              size="small"
            />
            <Input
              placeholder="Buscar categoria, linha ou grupo..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              prefix={<SearchOutlined style={{ color: '#aaa' }} />}
              style={{ width: 280 }}
              allowClear
              size="small"
            />
          </Space>
        </Space>

        {countByStatus.pendente > 0 && filtroStatus === 'pendente' && (
          <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message={`${countByStatus.pendente} categoria(s) pendente(s)`}
            description='Classifique cada uma como "Apresentar na DRE" (mapeada) ou "Não apresentar" (ignorada).'
          />
        )}

        <Table
          rowKey="categoria_contaazul"
          columns={columns}
          dataSource={filtradas}
          loading={loading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
          rowClassName={(r) => r.status === 'pendente' ? 'row-pendente' : ''}
          locale={{ emptyText: 'Nenhuma categoria neste filtro.' }}
          scroll={{ x: 'max-content' }}
        />
      </Space>

      {/* Modal edição */}
      <Modal
        open={!!editando}
        title={editando?.categoria_contaazul}
        onCancel={() => setEditando(null)}
        onOk={salvar}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={salvando}
        okButtonProps={{ icon: <CheckCircleOutlined /> }}
        width={520}
        destroyOnClose
      >
        <Form layout="vertical" size="small" style={{ marginTop: 8 }}>
          <Form.Item label="Status">
            <Radio.Group
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="mapeada">✓ Apresentar na DRE</Radio.Button>
              <Radio.Button value="ignorada">⊘ Não apresentar</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {form.status === 'mapeada' && (
            <>
              <Form.Item label="Grupo DRE" required>
                <Select
                  showSearch
                  placeholder="Selecione..."
                  value={form.grupo_dre || undefined}
                  onChange={(v) => setForm(f => ({ ...f, grupo_dre: v }))}
                  options={GRUPOS_OPCOES.map(g => ({ label: g, value: g }))}
                />
              </Form.Item>

              <Form.Item
                label="Linha DRE"
                required
                extra="Se uma linha com esse nome já existir no grupo, o valor se soma a ela."
              >
                <Input
                  placeholder="Ex.: Aluguel"
                  value={form.linha_dre}
                  onChange={e => setForm(f => ({ ...f, linha_dre: e.target.value }))}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
}

function StatusTag({ status }: { status: string }) {
  if (status === 'mapeada') return <Tag color="success" style={{ margin: 0 }}>✓ Na DRE</Tag>;
  if (status === 'pendente') return <Tag color="warning" style={{ margin: 0 }}>⚠ Pendente</Tag>;
  if (status === 'ignorada') return <Tag color="default" style={{ margin: 0 }}>⊘ Ignorada</Tag>;
  return <Tag style={{ margin: 0 }}>{status}</Tag>;
}

export default function Categorias() {
  return (
    <AntApp>
      <CategoriasInner />
    </AntApp>
  );
}
